import React, { useState, useEffect, useMemo } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CookieDisclaimer from "../components/CookieDisclaimer";
import SEO from "../components/SEO";
import AnimusHeroChrome from "../components/AnimusHeroChrome";
import { apiGet } from "../api/client";
import { getPlatformIcon } from "../utils/reviewPlatform";
import "../styles.css";

export default function Games() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(9);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Games");

  useEffect(() => {
    apiGet('/reviews')
      .then((data) => setGames(data))
      .catch((e) => setError(e.message || "Failed to load games"))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const baseCategories = games.flatMap(game => game.category).filter(cat => cat !== "Upcoming");
    return ["Upcoming", "All Games", "All Time Fav", ...new Set(baseCategories)];
  }, [games]);

  const gamesForFiltering = useMemo(() => [...games], [games]);

  let displayGames = [...gamesForFiltering];
  const isUpcoming = (game) => game.category.includes("Upcoming");

  if (selectedCategory === "All Time Fav") {
    displayGames = displayGames.filter(game => game.allTimeFav && !isUpcoming(game));
  } else if (selectedCategory === "All Games") {
    displayGames = displayGames.filter(game => !isUpcoming(game));
  } else if (selectedCategory === "Upcoming") {
    displayGames = displayGames.filter(isUpcoming);
  } else {
    displayGames = displayGames.filter(
      game => game.category.includes(selectedCategory) && !isUpcoming(game)
    );
  }
  if (searchTerm) {
    displayGames = displayGames.filter(game =>
      game.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
  const paginatedGames = displayGames.slice(0, visibleCount);

  const handleLoadMore = () => {
    setVisibleCount(v => v + 9);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setVisibleCount(9);
    setSearchTerm("");
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating / 2);
    const hasHalfStar = rating % 2 >= 1;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star"></i>);
    }
    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt"></i>);
    }
    const emptyStars = 5 - Math.ceil(rating / 2);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star"></i>);
    }

    return stars;
  };

  return (
    <>
      <SEO
        title="Game Reviews"
        description="Read ZeMaxi's honest game reviews! From Assassin's Creed to Persona, discover ratings and detailed reviews of games streamed on Twitch. Find your next game to play!"
        keywords="game reviews, ZeMaxi reviews, Twitch streamer reviews, gaming reviews, Assassin's Creed, Persona, RPG reviews, action game reviews"
        url="https://zemaxi.com/reviews"
      />
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="games-hero animus-hero animus-hero-bg">
          <AnimusHeroChrome />
          <div className="container">
            <p className="animus-hero-id font-mono">{'// SECTION_REVIEWS'}</p>
            <h1>Game <span className="highlight">Reviews</span></h1>
            <p>Games I've streamed / will be streaming and my personal reviews</p>
          </div>
        </section>
        <section className="videos-section page-body-section">
          <div className="container">
            <div className="games-search">
              <div className="search-filter-row">
                <div className="search-container">
                  <i className="fas fa-search search-icon"></i>
                  <input
                    type="text"
                    placeholder="Search games by name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button
                      className="search-clear"
                      onClick={() => setSearchTerm("")}
                      aria-label="Clear search"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                <div className="category-dropdown-wrapper">
                  <select
                    className={`category-dropdown${selectedCategory === 'All Time Fav' ? ' category-dropdown--fav' : ''}`}
                    value={selectedCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <i className="fas fa-chevron-down category-dropdown-arrow"></i>
                </div>
              </div>
            </div>
            <div className="games-list">
              {loading && (
                <div className="video-loading">
                  <div className="spinner"></div>
                  <p>Loading game reviews...</p>
                </div>
              )}
              {error && (
                <div className="video-loading">
                  <p style={{ color: 'red' }}>Failed to load games: {error}</p>
                </div>
              )}
              {!loading && !error && paginatedGames.length === 0 && (
                <div className="video-loading">
                  <p>No games found.</p>
                </div>
              )}
              {!loading && !error && paginatedGames.map(game => {
                const platformIcon = game.platform ? getPlatformIcon(game.platform) : null;
                return (
                <div className={`game-review-item ${game.allTimeFav ? 'all-time-fav' : ''} ${game.category.includes('Upcoming') ? 'upcoming-game' : ''}`} key={game.id} style={{'--poster-bg': `url(${game.poster})`}}>
                  {game.allTimeFav && (
                    <div className="all-time-fav-label">All Time Fav</div>
                  )}
                  <div className="game-content-wrapper">
                    <div className="game-poster">
                      <img src={game.poster} alt={game.title} onError={(e) => {
                        e.target.src = '/images/placeholder-game.jpg';
                      }} />
                      {game.vodUrl && (
                        <div className="game-vod-link desktop-only">
                          <a
                            href={game.vodUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="vod-link animus-vod-link"
                          >
                            <i className="fab fa-youtube" aria-hidden="true" />
                            <span>Watch VOD</span>
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="game-text-content">
                      <div className="game-header">
                        <h3>{game.title}</h3>
                        <div className="game-meta">
                          {game.category.map((cat, index) => (
                            <span key={index} className="review-chip review-chip--genre">
                              {cat}
                            </span>
                          ))}
                          <span className="review-chip review-chip--year">
                            {game.releaseYear}
                          </span>
                          {platformIcon && (
                            <span
                              className={`review-chip review-chip--platform review-chip--platform-${platformIcon.type}`}
                            >
                              <i className={platformIcon.icon} aria-hidden="true" />
                              <span>{game.platform}</span>
                            </span>
                          )}
                          {game.streamed && (
                            <span className="review-chip review-chip--streamed">
                              <i className="fas fa-circle" aria-hidden="true" />
                              <span>Streamed</span>
                            </span>
                          )}
                          {game.platinumed && (
                            <span className="review-chip review-chip--platinum">
                              <i className="fas fa-trophy" aria-hidden="true" />
                              <span>Platinumed</span>
                            </span>
                          )}
                        </div>
                        <div className="game-rating">
                          <div className="stars">
                            {game.rating ? renderStars(game.rating) : (
                              <span className="rating-placeholder">Review Coming Soon</span>
                            )}
                          </div>
                          <span className="rating-number">
                            {game.rating ? `${game.rating}/10` : ''}
                          </span>
                        </div>
                      </div>
                      <div className="game-review-content">
                        {Array.isArray(game.review) ? (
                          game.review.map((paragraph, index) => (
                            <p key={index} style={{ marginBottom: index < game.review.length - 1 ? '1rem' : '0' }}>
                              {paragraph}
                            </p>
                          ))
                        ) : (
                          <p dangerouslySetInnerHTML={{ __html: game.review }} />
                        )}
                      </div>
                      {game.vodUrl && (
                        <div className="game-vod-link mobile-only">
                          <a
                            href={game.vodUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="vod-link animus-vod-link"
                          >
                            <i className="fab fa-youtube" aria-hidden="true" />
                            <span>Watch VOD</span>
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
            {!loading && !error && paginatedGames.length < displayGames.length && (
              <div className="load-more-container">
                <button className="btn btn-primary" onClick={handleLoadMore}>Load More</button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
